import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { GARAGE_SIZES, HEAR_ABOUT, CITY_OPTIONS } from "../data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadForm = ({ dark = true, compact = false, sourceTag = "", prefill = null }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", garage_size: "", city: "", hear_about: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (prefill) setForm((f) => ({ ...f, ...prefill }));
  }, [prefill]);

  const inputCls = dark
    ? "w-full bg-white/5 border border-white/20 text-white placeholder-gray-500 p-3.5 focus:ring-2 focus:ring-[#028090] focus:border-transparent transition-all outline-none text-sm"
    : "w-full bg-white border border-gray-300 text-[#000000] placeholder-gray-400 p-3.5 focus:ring-2 focus:ring-[#028090] focus:border-transparent transition-all outline-none text-sm";
  const selectCls = `${inputCls} appearance-none`;
  const labelCls = `block text-xs uppercase tracking-wider font-semibold mb-1.5 ${dark ? "text-gray-400" : "text-gray-600"}`;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone, and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source_page: `${window.location.pathname}${sourceTag ? ` (${sourceTag})` : ""}` });
      setDone(true);
      toast.success("Request received! We'll reach out within one business day.");
    } catch (err) {
      toast.error("Something went wrong. Please call us at 480-351-6577.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className={`p-10 text-center ${dark ? "bg-white/5 border border-white/10" : "bg-teal-50 border border-teal-200"}`} data-testid="lead-form-success">
        <CheckCircle2 size={48} className="text-[#028090] mx-auto mb-4" strokeWidth={1.5} />
        <h3 className={`font-display text-2xl font-bold mb-2 ${dark ? "text-white" : "text-[#000000]"}`}>You're on the list!</h3>
        <p className={dark ? "text-gray-300" : "text-gray-600"}>We'll contact you within one business day to schedule your free in-home estimate.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="lead-form">
      <div className={compact ? "sm:col-span-2" : ""}>
        <label className={labelCls}>Name *</label>
        <input data-testid="lead-form-name" className={inputCls} placeholder="Your full name" value={form.name} onChange={set("name")} required />
      </div>
      <div>
        <label className={labelCls}>Phone *</label>
        <input data-testid="lead-form-phone" type="tel" className={inputCls} placeholder="(480) 555-0123" value={form.phone} onChange={set("phone")} required />
      </div>
      <div>
        <label className={labelCls}>Email *</label>
        <input data-testid="lead-form-email" type="email" className={inputCls} placeholder="you@email.com" value={form.email} onChange={set("email")} required />
      </div>
      <div>
        <label className={labelCls}>Garage Size</label>
        <select data-testid="lead-form-garage-size" className={selectCls} value={form.garage_size} onChange={set("garage_size")}>
          <option value="">Select size…</option>
          {GARAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className={labelCls}>City</label>
        <select data-testid="lead-form-city" className={selectCls} value={form.city} onChange={set("city")}>
          <option value="">Select city…</option>
          {CITY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls}>How did you hear about us?</label>
        <select data-testid="lead-form-hear-about" className={selectCls} value={form.hear_about} onChange={set("hear_about")}>
          <option value="">Select one…</option>
          {HEAR_ABOUT.map((h) => <option key={h} value={h}>{h}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={loading}
          data-testid="lead-form-submit-button"
          className="w-full bg-[#028090] text-white hover:bg-[#026c7a] disabled:opacity-60 transition-colors duration-300 px-8 py-4 uppercase font-bold tracking-wider flex items-center justify-center gap-2"
        >
          {loading && <Loader2 size={18} className="animate-spin" />}
          Get My Free Garage Quote
        </button>
        <p className={`text-xs mt-3 text-center ${dark ? "text-gray-500" : "text-gray-400"}`}>No pressure. No obligation. We'll never share your info.</p>
      </div>
    </form>
  );
};
